import { useOrigenStore } from '../Services/Providers/origenStore';
import { EnvConfig } from '../types/EnvConfig';
import { getConfigUrl } from './configUrl';
import { aplicarFlags, esHostLocal, FlagsBff, obtenerFlagsBff, sinFlags } from './featureFlags';

const VALIDITY_DURATION_MS = 60 * 60 * 1000;

/**
 * Bug 22942: los flags efectivos (`auto_via_bff`, `landings_enabled`,
 * `retomar_enabled`, `venta_presencial_enabled`, `*_mock`) se le piden al BFF
 * UNA vez por carga de página y se aplican sobre el config, venga de
 * origen-storage o de `react_config.json`. Nunca se persisten: antes se
 * guardaban ya resueltos durante 1 h, y si el BFF apagaba Auto el navegador
 * seguía con `auto_via_bff=true` (pegándole a /auto/v1/*, 404) aunque se
 * recargara. Un origen-storage viejo que todavía los tenga se ignora (se
 * limpian al leerlo).
 */
let flagsDeEstaCarga: Promise<FlagsBff | null> | null = null;
/** Solo localhost: el `react_config.json` crudo (sus flags son el default de desarrollo). */
let configLocalDeEstaCarga: Promise<EnvConfig> | null = null;

/** Solo para tests: simula una carga de página nueva. */
export const reiniciarCargaDePagina = (): void => {
  flagsDeEstaCarga = null;
  configLocalDeEstaCarga = null;
};

const bajarConfig = async (): Promise<EnvConfig> => {
  const response = await fetch(getConfigUrl(), { cache: 'no-store' });
  if (!response.ok) throw new Error('No se pudo obtener el archivo de configuración.');
  return response.json();
};

/** Config base (sin flags resueltos). Fuera de localhost usa origen-storage como antes. */
const configBase = async (local: boolean): Promise<EnvConfig> => {
  if (local) {
    // En localhost los flags del json son el default si no hay BFF: se lee el
    // json (una vez por carga) en vez de la cache, que no guarda flags.
    if (!configLocalDeEstaCarga) {
      configLocalDeEstaCarga = bajarConfig().catch((error) => {
        configLocalDeEstaCarga = null;
        throw error;
      });
    }
    return configLocalDeEstaCarga;
  }

  const { envConfig, setEnvConfig, lastLoaded } = useOrigenStore.getState();
  if (envConfig && lastLoaded) {
    const timeElapsed = Date.now() - lastLoaded;
    if (timeElapsed < VALIDITY_DURATION_MS) return sinFlags(envConfig);
    console.warn(`La configuración ha expirado (${Math.round(timeElapsed / VALIDITY_DURATION_MS)} días). Solicitando nueva versión...`);
  }

  const config = sinFlags(await bajarConfig());
  setEnvConfig(config);
  return config;
};

interface OpcionesCarga {
  /** Solo tests: por defecto `window.location.hostname`. */
  hostname?: string;
}

export const loadConfig = async (opciones: OpcionesCarga = {}): Promise<EnvConfig | null> => {
  const hostname = opciones.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const local = esHostLocal(hostname);

  try {
    const base = await configBase(local);
    if (!flagsDeEstaCarga) {
      // Misma base que getBffBaseUrl: react_config manda, .env como fallback de dev.
      // obtenerFlagsBff nunca tira: fail-closed (null => todo en false fuera de localhost).
      flagsDeEstaCarga = obtenerFlagsBff(base.bff_url || process.env.REACT_APP_API_BASE);
    }
    return aplicarFlags(base, await flagsDeEstaCarga, local);
  } catch (error) {
    console.error('Error al cargar el archivo de configuración:', error);
    return null;
  }
};
